import { cn } from '@/shared/utils/cn';
import { Visual } from '@/modules/Visual/Index';
import { Toggle } from '@/modules/Toggle';
import { IconType, IconFontColor } from '@/shared/types/types';
import React from 'react';
import { ListItemDefault } from '../molecules/ListItemDefault';
import { ListSeparator } from '../molecules/ListSeparator';

export interface ListUserProfileUser {
  name: string;
  email: string;
  avatar?: React.ReactNode | string;
  badge?: string;
}

export interface ListUserProfileMenuItem {
  id: string;
  label: string;
  icon?: IconType;
  color?: 'blue' | 'red';
  iconColor?: IconFontColor;
  type?: 'item' | 'toggle' | 'separator';
}

export interface ListUserProfileProps {
  user: ListUserProfileUser;
  menuItems: ListUserProfileMenuItem[];
  selectedItemId?: string;
  darkMode?: boolean;
  onMenuItemClick?: (itemId: string) => void;
  onDarkModeToggle?: (enabled: boolean) => void;
  className?: string;
}

export const ListUserProfile: React.FC<ListUserProfileProps> = ({
  user,
  menuItems,
  selectedItemId,
  darkMode = false,
  onMenuItemClick,
  onDarkModeToggle,
  className,
}) => {
  const renderAvatar = () => {
    if (typeof user.avatar === 'string') {
      return (
        <div className="ui-flex ui-h-32 ui-w-32 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-fill-brand ui-text-body-sm-regular ui-font-body-sm-semibold ui-text-on-color">
          {user.avatar}
        </div>
      );
    }
    return (
      user.avatar || (
        <div className="ui-flex ui-h-32 ui-w-32 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-fill-brand ui-text-body-sm-regular ui-font-body-sm-semibold ui-text-on-color">
          {user.name.substring(0, 2).toUpperCase()}
        </div>
      )
    );
  };

  return (
    <div
      className={cn(
        'ui-overflow-clip ui-rounded-surface-card ui-border ui-border-default ui-bg-surface ui-p-8',
        'ui-flex ui-w-[298px] ui-flex-col ui-gap-8',
        className,
      )}
    >
      {/* User Header */}
      <div className="ui-flex ui-h-56 ui-items-center ui-gap-8 ui-rounded-control-sm ui-bg-fill-hover ui-px-8 ui-py-6">
        {renderAvatar()}
        <div className="ui-min-w-0 ui-flex-1">
          <div className="ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
            {user.name}
          </div>
          <div className="ui-truncate ui-text-body-xs-regular ui-text-tertiary">{user.email}</div>
        </div>
        {user.badge && (
          <span className="ui-shrink-0 ui-rounded-control-sm ui-bg-fill-brand ui-px-8 ui-py-2 ui-text-body-xs-regular ui-font-body-xs-semibold ui-text-on-color">
            {user.badge}
          </span>
        )}
      </div>

      {/* Menu Items */}
      <ul className="ui-m-0 ui-flex ui-list-none ui-flex-col ui-gap-8 ui-p-0">
        {menuItems.map((item, index) => {
          if (item.type === 'separator') {
            return <ListSeparator key={`separator-${index}`} />;
          }

          if (item.type === 'toggle') {
            return (
              <li
                key={item.id}
                className="ui-flex ui-h-32 ui-items-center ui-justify-between ui-rounded-control-sm ui-px-8 ui-py-6"
              >
                <div className="ui-flex ui-items-center ui-gap-8">
                  {item.icon && (
                    <span className="ui-flex ui-items-center ui-justify-center ui-text-tertiary">
                      <Visual
                        slot={item.icon}
                        colorIcon="tertiary"
                        size="sm"
                        defaultSizeIcon={false}
                      />
                    </span>
                  )}
                  <span className="ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
                    {item.label}
                  </span>
                </div>
                <Toggle
                  checked={darkMode}
                  onChange={(checked) => onDarkModeToggle?.(checked)}
                  ariaLabel={item.label}
                />
              </li>
            );
          }

          return (
            <ListItemDefault
              key={item.id}
              icon={item.icon}
              iconColor={item.iconColor}
              current={selectedItemId === item.id}
              color={item.color}
              onClick={() => onMenuItemClick?.(item.id)}
            >
              {item.label}
            </ListItemDefault>
          );
        })}
      </ul>
    </div>
  );
};
